// libs
import { clearAccessToken, setAccessToken } from 'lib/auth'
// constants
import {
  SessionState,
  AuthChannelEvent,
  MAX_TIMEOUT_MS,
  ACTIVITY_THROTTLE_MS,
  REFRESH_BEFORE_EXPIRY_MS,
} from './constants'
// types
import type { RefreshTokenResponse } from './types'
// utils
import { decodeJwtExp } from './utils'
import { isNetworkError } from 'utils/network'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MutationFn<T = unknown> = (options?: Record<string, unknown>) => Promise<{ data?: T }>
type ShowModalFn = (data: { onContinue: () => void; onLogout: () => void }) => void
type RouterPushFn = (url: string) => void | Promise<void>

enum Timer {
  REFRESH = 'refreshTimer',
  WARNING = 'warningTimer',
  INACTIVITY = 'inactivityTimer'
}

export interface SessionManagerDeps {
  refreshMutation: MutationFn<RefreshTokenResponse>
  showSessionExpiringModal: ShowModalFn
  hideSessionExpiringModal: () => void
  authChannel: BroadcastChannel | null
  routerPush: RouterPushFn
  dispatchLogout: () => void
  inactivityTimeoutMs?: number
  warningTimeoutMs?: number
  /** Returns the absolute TTL of the refresh token in ms (user/company setting, or default 12h) */
  getAbsoluteTtlMs: () => number
  /** Called after the session is successfully recovered from an offline state */
  onNetworkRecovered?: () => void
}

// ---------------------------------------------------------------------------
// Session lifecycle:
//
//   1. init()           — called once when the user is authenticated
//   2. scheduleRefresh  — sets a timer to refresh the token before JWT expiry
//   3. resetInactivity  — resets the idle/warning timers on every user activity
//   4. handleActivity   — throttled handler for DOM events (click, scroll, etc.)
//
//  Session lifecycle by session state:
//   ACTIVE → OFFLINE_ACTIVE   when network drops (or NetworkError from refresh)
//   OFFLINE_ACTIVE → REFRESHING → ACTIVE   when network returns and refresh succeeds
//   OFFLINE_ACTIVE → TERMINATED   when refresh fails with auth error on reconnect
//   OFFLINE_EXPIRED → TERMINATED  when absolute TTL passed while offline
//   ACTIVE → TERMINATED          on inactivity, auth error, or user logout
//
// Cross-tab communication via BroadcastChannel.
// Token refresh coordinated via navigator.locks (one tab at a time).
// ---------------------------------------------------------------------------

export class SessionManager {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private warningTimer: ReturnType<typeof setTimeout> | null = null
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null

  private lastActivityAt = Date.now()
  private sessionState: SessionState = SessionState.ACTIVE
  private absoluteTtlExpiresAt: number | null = null
  private accessTokenExpiresAt: number | null = null
  private isInitialized = false
  private deps: SessionManagerDeps

  /** Stored when warning modal is open — allows remote activity to silently continue */
  private pendingContinue: (() => void) | null = null

  constructor(deps: SessionManagerDeps) {
    this.deps = deps
  }

  /** React deps change on every render — keep them in sync via useEffect */
  updateDeps(deps: SessionManagerDeps) {
    this.deps = deps
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Bootstrap the session: record absolute TTL deadline, schedule the first
   * token refresh, and start tracking user activity for inactivity timeout.
   */
  init(token: string) {
    const absoluteTtlMs = this.deps.getAbsoluteTtlMs()

    this.absoluteTtlExpiresAt = Date.now() + absoluteTtlMs
    this.isInitialized = true
    this.scheduleRefresh(token)
    this.resetInactivity()
  }

  restartInactivity() {
    if (!this.isInitialized || this.sessionState === SessionState.TERMINATED) return

    this.resetInactivity()
  }

  /**
   * Throttled handler for DOM activity events.
   * Resets inactivity timers and notifies other tabs.
   * lastActivityAt is used here for the throttle check — it must stay in sync.
   */
  handleActivity() {
    const now = Date.now()

    if (now - this.lastActivityAt < ACTIVITY_THROTTLE_MS) {
      return
    }

    this.lastActivityAt = now
    this.resetInactivity()
    this.deps.authChannel?.postMessage({ type: AuthChannelEvent.ACTIVITY })
  }

  /**
   * Apply a token received from another tab (via BroadcastChannel TOKEN_REFRESHED).
   * Re-schedules refresh and resets inactivity without making a network call.
   */
  syncFromOtherTab(token: string) {
    setAccessToken(token)
    this.scheduleRefresh(token)
    this.resetInactivity(true)
  }

  // ---------------------------------------------------------------------------
  // Network awareness
  // ---------------------------------------------------------------------------

  /**
   * Called when the browser fires the 'offline' event (or when a refresh attempt
   * fails with a NetworkError). Pauses all timers to preserve the session while
   * the user is working offline.
   */
  goOffline() {
    if (
      this.sessionState === SessionState.TERMINATED ||
      this.sessionState === SessionState.OFFLINE_ACTIVE ||
      this.sessionState === SessionState.OFFLINE_EXPIRED
    ) {
      return // idempotent
    }

    this.sessionState = SessionState.OFFLINE_ACTIVE
    this.clearAllTimers()

    // Close the warning modal if it was open — timers are paused and
    // the session will resume automatically when network returns
    if (this.pendingContinue !== null) {
      this.pendingContinue = null
      this.deps.hideSessionExpiringModal()
    }
  }

  /**
   * Called when the browser fires the 'online' event.
   * Refreshes the token to verify the session is still alive on the server,
   * then resumes inactivity timers and notifies the sync container.
   */
  async goOnline() {
    if (
      this.sessionState === SessionState.TERMINATED ||
      this.sessionState === SessionState.REFRESHING
    ) return

    if (this.sessionState === SessionState.OFFLINE_EXPIRED) {
      await this.terminate()
      return
    }

    // Fast-fail if we already know the absolute TTL has passed —
    // the server will reject any refresh attempt anyway
    if (Date.now() > this.absoluteTtlExpiresAt!) {
      this.sessionState = SessionState.OFFLINE_EXPIRED
      await this.terminate()
      return
    }

    // If the access token expired while offline the refresh timer never fired.
    // The backend requires a valid (non-expired) access token to issue a new one,
    // so a refresh attempt would fail — terminate immediately.
    if (Date.now() > this.accessTokenExpiresAt!) {
      this.sessionState = SessionState.OFFLINE_EXPIRED
      await this.terminate()
      return
    }

    this.sessionState = SessionState.REFRESHING
    const success = await this.refreshToken()

    if (success) {
      this.resumeTimers()
      this.notifySessionRecovered()
    }
    // On failure: executeRefresh called either goOffline() (NetworkError — spurious online event)
    // or terminate() — session is truly dead
  }

  private notifySessionRecovered() {
    this.deps.onNetworkRecovered?.()
  }

  // ---------------------------------------------------------------------------
  // Token refresh
  // ---------------------------------------------------------------------------

  /**
   * Schedule an automatic token refresh shortly before the JWT expires.
   */
  scheduleRefresh(token: string) {
    if (this.sessionState === SessionState.TERMINATED) {
      return
    }

    this.clearTimer(Timer.REFRESH)

    const expiresAt = decodeJwtExp(token)

    if (!expiresAt) {
      return
    }

    this.accessTokenExpiresAt = expiresAt

    const delay = Math.max(expiresAt - REFRESH_BEFORE_EXPIRY_MS - Date.now(), 0)

    this.refreshTimer = setTimeout(() => {
      this.refreshToken()
    }, delay)
  }

  /**
   * Call authRefreshToken, store the new token, reschedule, and broadcast.
   * Uses navigator.locks to ensure only one tab refreshes at a time —
   * other tabs receive the new token via TOKEN_REFRESHED broadcast.
   */
  async refreshToken(): Promise<boolean> {
    if (this.sessionState === SessionState.TERMINATED) {
      return false
    }

    return navigator.locks.request('skefto-token-refresh', { ifAvailable: true }, async (lock) => {
      // If lock is null, another tab is already refreshing — skip
      // Return true so the caller (e.g. goOnline) knows the operation is covered.
      if (!lock) {
        return true
      }

      return this.executeRefresh()
    })
  }

  private async executeRefresh(): Promise<boolean> {
    if (this.sessionState === SessionState.TERMINATED) {
      return false
    }

    try {
      const { data } = await this.deps.refreshMutation()

      if (data?.authRefreshToken?.ok) {
        const newToken = data.authRefreshToken.data.accessToken

        setAccessToken(newToken)
        this.sessionState = SessionState.ACTIVE
        this.scheduleRefresh(newToken)
        this.deps.authChannel?.postMessage({
          type: AuthChannelEvent.TOKEN_REFRESHED,
          accessToken: newToken
        })

        return true
      }
    } catch (error) {
      if (isNetworkError(error)) {
        // Network dropped — pause the session, do not terminate
        this.goOffline()
        return false
      }
    }

    // Refresh failed — session is dead on the server (expired / revoked / reused).
    // ErrorLink will handle Redux cleanup and hard redirect for GraphQL errors,
    // but we still call terminate() to clean up timers and notify other tabs.
    await this.terminate()

    return false
  }

  // ---------------------------------------------------------------------------
  // Inactivity tracking
  // ---------------------------------------------------------------------------

  /**
   * Reset inactivity countdown. Called on every (throttled) user activity.
   *
   * Two timers run in parallel:
   *   - warningTimer → show "session expiring" modal
   *   - inactivityTimer → force terminate
   *
   * While offline: only records lastActivityAt. Timers are not started —
   * resumeTimers() will give a fresh window when back online.
   *
   * @param closeModalIfOpen - Pass `true` when called due to activity in ANOTHER tab
   *   (via BroadcastChannel ACTIVITY or TOKEN_REFRESHED). This dismisses the warning modal
   *   automatically because the user is provably active elsewhere.
   *   Pass `false` (default) for local DOM activity — the modal stays open so the user
   *   must explicitly click "Continue" or "Log Out" in the current tab.
   */
  resetInactivity(closeModalIfOpen = false) {
    const modalIsOpen = this.pendingContinue !== null

    if (closeModalIfOpen && modalIsOpen) {
      this.deps.hideSessionExpiringModal()
      this.pendingContinue = null
    }

    this.lastActivityAt = Date.now()

    if (this.sessionState === SessionState.OFFLINE_ACTIVE || this.sessionState === SessionState.OFFLINE_EXPIRED) {
      // Timers are paused while offline — just keep lastActivityAt up to date
      return
    }

    this.clearTimer(Timer.WARNING)
    this.clearTimer(Timer.INACTIVITY)

    const safeInactivityMs = Math.min(this.deps.inactivityTimeoutMs, MAX_TIMEOUT_MS)
    const safeWarningMs = Math.min(this.deps.warningTimeoutMs, MAX_TIMEOUT_MS)

    // Don't schedule a new warning while the modal is still visible —
    // the user must interact with it first.
    if (!modalIsOpen || closeModalIfOpen) {
      this.warningTimer = setTimeout(() => {
        const onContinue = async () => {
          this.pendingContinue = null

          const success = await this.refreshToken()

          if (success) {
            this.resetInactivity()
          }
        }

        this.pendingContinue = onContinue

        this.deps.showSessionExpiringModal({
          onContinue: () => {
            this.clearTimer(Timer.INACTIVITY)
            onContinue()
          },
          onLogout: () => {
            this.pendingContinue = null
            this.terminate()
          }
        })
      }, safeWarningMs)
    }

    // Force logout after full inactivity timeout
    this.inactivityTimer = setTimeout(() => {
      this.terminate()
    }, safeInactivityMs)
  }

  /**
   * Restart inactivity timers after coming back online.
   * Timers were paused as a courtesy while offline — the user gets a
   * full fresh inactivity window on reconnect.
   */
  private resumeTimers() {
    this.resetInactivity()
  }

  // ---------------------------------------------------------------------------
  // Termination
  // ---------------------------------------------------------------------------

  /**
   * End the session: clear all timers, wipe the token, notify other tabs,
   * and redirect to login. Guarded against multiple calls.
   */
  async terminate() {
    if (this.sessionState === SessionState.TERMINATED) {
      return
    }

    this.sessionState = SessionState.TERMINATED
    this.clearAllTimers()
    this.deps.dispatchLogout()
    clearAccessToken()
    this.deps.authChannel?.postMessage({ type: AuthChannelEvent.LOGOUT })
    await this.deps.routerPush('/login')
  }

  // ---------------------------------------------------------------------------
  // Timer helpers
  // ---------------------------------------------------------------------------

  clearAllTimers() {
    this.clearTimer(Timer.REFRESH)
    this.clearTimer(Timer.WARNING)
    this.clearTimer(Timer.INACTIVITY)
  }

  private clearTimer(timer: Timer) {
    if (this[timer]) {
      clearTimeout(this[timer])
      this[timer] = null
    }
  }
}
